/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './settings.css';
import { CSVLink } from 'react-csv';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Search, Download, Upload } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import MemberBalanceRow from './MemberBalanceRow';
import useDebounce from '../../hooks/useDebounce';
import ImportModal from './ImportModal';

function TimeoffBalancePanel() {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [members, setMembers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const { debounce } = useDebounce();

  const headers = [
    { label: 'User Email', key: 'email' },
    { label: 'User Name', key: 'name' },
    { label: 'Policy', key: 'policy' },
    { label: 'Balance', key: 'balance' },
  ];

  const data = useMemo(() => {
    const res = [];
    members.forEach((member) => {
      policies.forEach((policy) => {
        const match = member.ptoBalances.find((balance) => balance.policyId === policy.id);
        res.push({
          email: member.user.email,
          name: `${member.user.firstName} ${member.user.lastName}`,
          policy: policy.name,
          balance: match ? match.balance : '',
        });
      });
    });
    return res;
  }, [members, policies]);

  const getMembers = useCallback(async (params) => {
    if (!activeWorkspace) return;
    setIsLoading(true);
    const resMember = await sendRequest({ method: 'get', url: API.WORKSPACE.membersBalances(activeWorkspace.id), params, timeout: 60000 });
    const resPolicy = await sendRequest({ method: 'get', url: API.WORKSPACE.offTimePolicies(activeWorkspace.id) });
    if (resPolicy.success && resMember.success) {
      setPolicies(resPolicy.data.ptoPolicies);
      setMembers(resMember.data?.members ?? []);
    } else {
      displayNotification({ message: 'An error occurs while fetching data.', severity: 'error' });
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(debounce((q) => setSearchQuery(q), 500), []);
  const handleSearchChange = (e) => { setSearchInput(e.target.value); handleSearch(e.target.value); };

  useEffect(() => {
    getMembers({ name: searchQuery || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const thBase = { padding: '12px 8px', letterSpacing: '0.05em' };
  const thCls = 'text-left text-[11px] font-semibold uppercase tracking-wider text-ink-500 whitespace-nowrap';

  return (
    <>
      {showImportModal && (
        <ImportModal closeModal={() => setShowImportModal(false)} getMembers={() => getMembers()} data={data} headers={headers} />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 pt-4 pb-3">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-ink-900 mb-0.5">Time off balances</h4>
          <p className="text-sm text-ink-500">Set up time off balances for members</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <div className="relative" style={{ height: 40, width: 240 }}>
            <Search size={14} className="text-ink-500 pointer-events-none" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search members"
              className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
              style={{ height: 40, paddingLeft: 36, paddingRight: 12, fontSize: 14, lineHeight: '20px', borderStyle: 'solid', boxSizing: 'border-box' }}
            />
          </div>
          <CSVLink data={data} headers={headers} filename="timeOffBalance.csv" style={{ textDecoration: 'none' }}>
            <span className="inline-flex items-center gap-1.5 box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>
              <Download size={16} />
              Export
            </span>
          </CSVLink>
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-1.5 box-border h-10 px-4 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition flex-shrink-0"
          >
            <Upload size={16} />
            Import
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: 640, borderCollapse: 'collapse' }}>
          <thead>
            <tr className="bg-surface-page">
              <th scope="col" className={thCls} style={{ ...thBase, paddingLeft: 16 }}>Member</th>
              {policies.map((policy) => (
                <th key={policy.id} scope="col" className={thCls} style={thBase}>{policy.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={policies.length + 1} className="text-center text-sm text-ink-500" style={{ padding: '48px 16px' }}>Loading…</td>
              </tr>
            ) : members?.length ? (
              members.map((member) => <MemberBalanceRow key={member.id} member={member} policies={policies} getMembers={() => getMembers()} />)
            ) : (
              <tr>
                <td colSpan={policies.length + 1} className="text-center text-sm text-ink-500" style={{ padding: '48px 16px' }}>No members found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TimeoffBalancePanel;
